import React from "react"
import SI, { Play, Spirit } from "../../Common/SI"
import { AdversaryNameLink } from "../Filtering/AdversaryNameLink"
import { SpiritLink } from "../Filtering/SpiritLink"
import { PlayBlock } from "../PlayBlock"
import Sorting from "../Sorting"

export interface SpiritAdversaryTableProps {
	plays: Play[]
}

export const SpiritAdversaryTable = React.memo(function (props: SpiritAdversaryTableProps): JSX.Element {

	const playsByAdversaryBySpirit: Map<Spirit, Map<string, TableCell>> = new Map()

	const maxAdversaryLevelByName: Map<string, number> = new Map(SI.adversaries.map(adversary => [adversary.name, adversary.difficulty.length - 1]))

	// Set up the data structure
	for (const spirit of SI.spirits) {
		const playsByAdversary: Map<string, TableCell> = new Map()
		playsByAdversaryBySpirit.set(spirit, playsByAdversary)

		for (const adversary of SI.adversaries) {
			playsByAdversary.set(adversary.name, {
				coldest: false,
				highestLevelPlayed: undefined,
				plays: [],
				victoryAtHighestLevel: false,
			})
		}
	}

	// Populate the plays
	for (let i = props.plays.length - 1; i >= 0; i--) {
		const play = props.plays[i]
		if (!SI.isSingleAdversaryNoScenario(play)) {
			continue
		}

		for (const playedSpirit of play.spirits) {
			const tableCell = playsByAdversaryBySpirit.get(playedSpirit.spirit)!.get(play.adversaries[0].name)!
			tableCell.plays.push(play)

			if (tableCell.highestLevelPlayed === undefined || play.adversaries[0].level > tableCell.highestLevelPlayed) {
				tableCell.highestLevelPlayed = play.adversaries[0].level
			}

			if (play.outcome.victory && !tableCell.victoryAtHighestLevel && play.adversaries[0].level === maxAdversaryLevelByName.get(play.adversaries[0].name)) {
				tableCell.victoryAtHighestLevel = true
			}
		}
	}

	// Find the coldest for each Spirit
	for (const spirit of SI.spirits) {
		const playsByAdversary = playsByAdversaryBySpirit.get(spirit)!
		let minHighestLevelPlayed: number | undefined = Infinity
		let fewestPlayedAtThatLevel: number = Infinity
		let beatenAllAtHighestLevel: boolean = true

		for (const adversary of SI.adversaries) {
			const tableCell = playsByAdversary.get(adversary.name)!
			beatenAllAtHighestLevel = beatenAllAtHighestLevel && tableCell.victoryAtHighestLevel

			if (tableCell.highestLevelPlayed === undefined) {
				minHighestLevelPlayed = undefined
				fewestPlayedAtThatLevel = 0
			} else if (minHighestLevelPlayed !== undefined) {
				if (tableCell.highestLevelPlayed < minHighestLevelPlayed) {
					minHighestLevelPlayed = tableCell.highestLevelPlayed
					fewestPlayedAtThatLevel = Infinity
				}
				if (tableCell.highestLevelPlayed === minHighestLevelPlayed) {
					fewestPlayedAtThatLevel = Math.min(fewestPlayedAtThatLevel, tableCell.plays.filter(play => play.adversaries[0]!.level === tableCell.highestLevelPlayed).length)
				}
			}
		}

		for (const adversary of SI.adversaries) {
			const tableCell = playsByAdversary.get(adversary.name)!
			tableCell.coldest = (beatenAllAtHighestLevel || !tableCell.victoryAtHighestLevel)
				&& tableCell.highestLevelPlayed === minHighestLevelPlayed && tableCell.plays.filter(play => play.adversaries[0]!.level === tableCell.highestLevelPlayed).length === fewestPlayedAtThatLevel
		}
	}

	// Order the Spirits nicely
	// TODO: Make this the initial order of SI.spirits?
	const orderableSpirits = SI.spirits.map(spirit => {
		return {
			spirit,
			spiritName: spirit.nickname,
			expansionOrder: SI.expansionOrder.get(spirit.expansion),
		}
	})
	Sorting.sort(orderableSpirits, { field: 'expansionOrder', ascending: true }, 'spiritName')

	const orderedSpirits = orderableSpirits.map(os => os.spirit)

	return (
		<div>
			<table className='stats spirit-adversary'>
				<thead>
					<tr>
						<th>(Single adversary,<br />no scenario)</th>
						{
							SI.adversaries.map(adversary => (
								<th key={adversary.name}><AdversaryNameLink adversaryName={adversary.name} /></th>
							))
						}
					</tr>
				</thead>
				<tbody>
					{
						orderedSpirits.map(spirit => (
							<tr key={spirit.name}>
								<th><SpiritLink spirit={spirit} /></th>
								{
									SI.adversaries.map(adversary => (
										<td key={adversary.name} className={`${playsByAdversaryBySpirit.get(spirit)!.get(adversary.name)!.coldest ? 'coldest' : ''} ${playsByAdversaryBySpirit.get(spirit)!.get(adversary.name)!.victoryAtHighestLevel ? `maxed` : ''}`}>
											{
												playsByAdversaryBySpirit.get(spirit)!.get(adversary.name)!.coldest ? <div className="cell-badge" title="This Spirit needs practice against this Adversary"></div> : null
											}
											<div className="play-blocks">
												{
													playsByAdversaryBySpirit.get(spirit)!.get(adversary.name)!.plays.map(play => (
														<PlayBlock play={play} key={play.number} />
													))
												}
											</div>
										</td>
									))
								}
							</tr>
						))
					}
				</tbody>
			</table>
		</div>
	)
})

interface TableCell {
	coldest: boolean
	highestLevelPlayed: number | undefined
	plays: Play[]
	victoryAtHighestLevel: boolean
}
